import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import contractsAPI from "../../../../services/contracts";
import schoolsAPI from "../../../../services/schools";
import { replaceMailPreviewCidImages } from "../../../../utils/mailPreview";

const SendPaymentSituationModal = forwardRef(({ schoolId, trainingId, accessToken, toast }, ref) => {
    const [visible, setVisible] = useState(false);
    const [contractId, setContractId] = useState(null);
    const [sending, setSending] = useState(false);
    const [schoolAvatar, setSchoolAvatar] = useState("");

    // Fetch email preview from backend (only enabled when dialog is visible and contractId is set)
    const previewQuery = contractsAPI.GetPaymentSituationEmailPreview(
        schoolId,
        trainingId,
        accessToken,
        contractId,
        visible && !!contractId
    );
    const previewBody = useMemo(
        () => replaceMailPreviewCidImages(previewQuery.data?.body || "", schoolAvatar),
        [previewQuery.data?.body, schoolAvatar]
    );

    useEffect(() => {
        if (!schoolId) {
            setSchoolAvatar("");
            return;
        }
        schoolsAPI.GetOneSchool(schoolId).then((data) => {
            setSchoolAvatar(data?.data?.avatar || "");
        });
    }, [schoolId]);

    useImperativeHandle(ref, () => ({
        show(id) {
            setContractId(id);
            setVisible(true);
        },
        hide() {
            setVisible(false);
            setContractId(null);
        }
    }));

    const handleSend = async () => {
        if (!contractId) return;
        setSending(true);
        try {
            const result = await contractsAPI.SendPaymentSituationEmail(
                schoolId,
                trainingId,
                accessToken,
                contractId
            );
            toast.current?.show({
                severity: "success",
                summary: "E-mail envoyé",
                detail: result?.message || "Le mail de situation a été envoyé aux parents et élèves concernés.",
                life: 4000,
            });
            setVisible(false);
            setContractId(null);
        } catch (err) {
            console.error("Error sending payment situation email:", err);
            toast.current?.show({
                severity: "error",
                summary: "Erreur",
                detail: err?.message || "Impossible d'envoyer l'e-mail de situation de paiements.",
                life: 4000,
            });
        } finally {
            setSending(false);
        }
    };

    const handleHide = () => {
        if (!sending) {
            setVisible(false);
            setContractId(null);
        }
    };

    const dialogFooter = (
        <div className="flex justify-content-end gap-2 mt-3">
            <Button
                label="Annuler"
                icon="pi pi-times"
                className="p-button-text p-button-secondary"
                onClick={handleHide}
                disabled={sending}
            />
            <Button
                label="Confirmer et envoyer"
                icon="pi pi-send"
                className="p-button-primary"
                onClick={handleSend}
                loading={sending}
                disabled={previewQuery.isLoading || previewQuery.isError}
            />
        </div>
    );

    return (
        <Dialog
            header="Envoyer la situation des paiements"
            visible={visible}
            style={{ width: "55vw", maxWidth: "900px" }}
            modal
            footer={dialogFooter}
            onHide={handleHide}
            dismissableMask={!sending}
            closeOnEscape={!sending}
        >
            {previewQuery.isLoading ? (
                <div className="flex flex-column align-items-center justify-content-center py-6">
                    <ProgressSpinner style={{ width: "50px", height: "50px" }} strokeWidth="8" fill="var(--surface-ground)" animationDuration=".5s" />
                    <p className="mt-3 text-600 font-semibold">Génération de la situation de paiements...</p>
                </div>
            ) : previewQuery.isError ? (
                <div className="flex flex-column align-items-center justify-content-center py-6 text-red-500">
                    <i className="pi pi-exclamation-triangle text-4xl mb-3"></i>
                    <p className="font-bold">Erreur de chargement</p>
                    <p className="text-sm mt-1">{previewQuery.error?.message || "Impossible de générer l'aperçu du mail."}</p>
                </div>
            ) : (
                <div className="flex flex-column gap-3">
                    <div className="p-3 bg-bluegray-50 border-round border-1 border-bluegray-100 flex flex-column gap-2 text-sm text-800">
                        <div>
                            <span className="font-bold">Destinataires : </span>
                            <span>
                                {previewQuery.data?.recipients && previewQuery.data.recipients.length > 0 
                                    ? previewQuery.data.recipients.join(", ") 
                                    : <span className="text-red-500 italic">Aucune adresse e-mail trouvée pour ce contrat</span>}
                            </span>
                        </div>
                        <div>
                            <span className="font-bold">Objet : </span>
                            <span>{previewQuery.data?.subject || "Situation de vos règlements"}</span>
                        </div>
                    </div>

                    <div className="flex flex-column gap-1">
                        <span className="font-bold text-sm text-600">Aperçu du contenu de l'e-mail :</span>
                        <iframe
                            title="Aperçu e-mail situation de paiements"
                            srcDoc={previewBody}
                            style={{
                                width: "100%",
                                height: "420px",
                                border: "1px solid var(--surface-border)",
                                borderRadius: "8px",
                                backgroundColor: "#ffffff"
                            }}
                        />
                    </div>
                </div>
            )}
        </Dialog>
    );
});

export default SendPaymentSituationModal;
